import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { diseases, doctorInfo } from '../mockData';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, 
  AlertCircle, 
  Stethoscope, 
  CheckCircle,
  Phone,
  Calendar
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';

const DiseaseDetail = () => {
  const { diseaseId } = useParams();
  const navigate = useNavigate();
  const disease = diseases.find(d => d.id === diseaseId);

  if (!disease) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4 text-gray-900 dark:text-slate-50">Condition not found</h1>
          <Button onClick={() => navigate('/conditions')}>
            View All Conditions
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <main className="pt-24 pb-16 bg-white dark:bg-slate-900">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-blue-600 to-blue-700 py-16">
          <div className="max-w-7xl mx-auto px-6">
            <Link to="/conditions" className="inline-flex items-center text-white/80 hover:text-white mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to All Conditions
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {disease.title}
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl">
              {disease.shortDesc}
            </p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-slate-50">Symptoms</h2>
                  </div>
                  <ul className="space-y-3">
                    {disease.symptoms.map((symptom, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                        <span className="text-gray-700 dark:text-slate-300">{symptom}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                      <Stethoscope className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-slate-50">Causes & Risk Factors</h2>
                  </div>
                  <ul className="space-y-3">
                    {disease.causes.map((cause, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                        <span className="text-gray-700 dark:text-slate-300">{cause}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-slate-50">Treatment Approach</h2>
                  </div>
                  <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
                    {disease.treatment}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-900/10 border-blue-200 dark:border-blue-800">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-slate-50 mb-4">
                    Get Expert Treatment
                  </h3>
                  <p className="text-gray-600 dark:text-slate-300 mb-6">
                    Dr. Akshay Kawedia provides comprehensive care for {disease.title.toLowerCase()}. Book a consultation today.
                  </p>
                  <div className="space-y-3">
                    <Link to="/#contact">
                      <Button className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 text-white">
                        <Calendar className="w-4 h-4 mr-2" />
                        Book Appointment
                      </Button>
                    </Link>
                    <a href={`tel:${doctorInfo.phone}`}>
                      <Button variant="outline" className="w-full">
                        <Phone className="w-4 h-4 mr-2" />
                        Call: {doctorInfo.phone}
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-slate-50 mb-4">
                    Other Conditions We Treat
                  </h3>
                  <ul className="space-y-2">
                    {diseases
                      .filter(d => d.id !== diseaseId)
                      .slice(0, 5)
                      .map(d => (
                        <li key={d.id}>
                          <Link 
                            to={`/conditions/${d.id}`}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                          >
                            {d.title}
                          </Link>
                        </li>
                      ))}
                  </ul>
                  <Link to="/conditions">
                    <Button variant="link" className="px-0 mt-2 text-blue-600 dark:text-blue-400">
                      View All Conditions →
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
};

export default DiseaseDetail;
